import { Injectable } from '@angular/core';
import {
  createSearchService,
  SearchService as LibrarySearchService,
} from 'qp-storefront';
import {
  Facet,
  Resource,
  ResourceVm,
  SearchRecord,
} from 'qp-ui-interface';
import { ConfigService } from '../shared/services/config.service';
import { LocalStorageService } from '../shared/services/storage/local-storage.service';
import { StorefrontPolicyService } from '../shared/services/firstlight/storefront-policy/storefront-policy.service';
import { resourceAdaptor } from 'qp-adaptor';
import { getDeviceType } from '../shared/utils/deviceUtils';
import { ErrorFactory } from '../shared/modules/errors/handler/ErrorFactory';
import { ErrorEvents } from '../shared/utils/ErrorUtils';
import { KEYS as STORAGE_KEYS } from '../shared/utils/constants/storage.constants';
import { IResourceList } from '../shared/common/interfaces/common.interface';

@Injectable()
export class SearchService {
  private readonly KEYS = {
    PLATFORM_TOKEN: STORAGE_KEYS.PLATFORM_TOKEN,
    X_AUTH_TOKEN: STORAGE_KEYS.EVERGENT_OVAT_TOKEN,
    X_AUTH_TOKEN_EXP: STORAGE_KEYS.EVERGENT_OVAT_TOKEN_EXPIRY,
  };

  private _instance: LibrarySearchService;

  constructor(
    private readonly localStorage: LocalStorageService,
    private readonly configService: ConfigService,
    private readonly policyService: StorefrontPolicyService  ) {}

  private init() {
    const { searchHistoryURL, noSearchResultsURL, vodMetaDataURL } =
      this.configService.applicationConfig;
    this._instance = createSearchService(
      searchHistoryURL,
      noSearchResultsURL,
      vodMetaDataURL,
      { ...this.policyService.getPolicy() }
    );
  }

  private service() {
    if (!this._instance) {
      this.init();
    }
    return this._instance;
  }

  refresh() {
    this._instance = null;
    this.init();
  }

  async addRecentSearch(recentSearch: string): Promise<void> {
    this.refresh();
    const xAuthToken = await this.getXAuthToken();
    return this.service().putRecentSearch(
      recentSearch,
      xAuthToken,
      this.getAuthToken()
    );
  }

  async getRecentSearches(): Promise<SearchRecord[]> {
    this.refresh();
    const xAuthToken = await this.getXAuthToken();
    return this.service().getRecentSearch(xAuthToken, this.getAuthToken());
  }

  private getAuthToken(): string {
    const authData = this.localStorage.getItem(this.KEYS.PLATFORM_TOKEN);
    return authData === 'undefined'
      ? this.configService.applicationConfig.basicAuthorization
      : authData;
  }

  private async getXAuthToken(): Promise<string> {
     return this.localStorage.getItem(this.KEYS.X_AUTH_TOKEN);
  }




  private mapResources(
    resources: Resource[],
    aspectRatio = '0-2x3'
  ): ResourceVm[] {
    const { imageResizeURL } = this.configService.applicationConfig;
    return resources.map((resource) =>
      resourceAdaptor(imageResizeURL, resource, getDeviceType(), aspectRatio)
    );
  }

  /*   getDataFromContentURL(
    contentURL: string,
    isHeader: boolean,
    disableMap?: boolean
  ): Observable<any> {
    if (!contentURL) {
      return from([undefined]);
    }
    const headers = this.createHeaders(isHeader);
    const url = this.buildContentURL(contentURL);

    return this.httpClient.get(url, isHeader ? { headers } : undefined).pipe(
      map((response) => this.processResponse(response, disableMap)),
      catchError((error) => {
        console.error('Error fetching content:', error);
        throw error;
      })
    );
  }
 */
  private addAllTermToFacet(
    facet: Facet | null,
    totalResources: number
  ): Facet | null {
    if (!facet) {
      return null;
    }

    // Facet has field and terms properties
    const allTerm = { count: totalResources, term: 'all' };
    return {
      ...facet,
      terms: [allTerm, ...(facet.terms || [])],
    };
  }

  public async getSearchResults(
    searchTerm: string,
    pageNumber: string = '1',
    pageSize: string = '50',
    facet: string = null
  ): Promise<IResourceList> {
    try {
      this.refresh();
      const result = await this.service().getSearchResults(
        searchTerm,
        pageNumber,
        pageSize,
        facet
      );
      if (result?.resources?.length) {
        const facet = this.addAllTermToFacet(
          result.facet,
          result.totalResources
        );
        return {
          resources: this.mapResources(result.resources, '0-16x9'),
          totalResources: result.totalResources,
          facet: facet,
        };
      }
      return {
        resources: [],
        totalResources: 0,
        facet: null,
      };
    } catch (error) {
      return Promise.reject(
        ErrorFactory.get(error, ErrorEvents.SEARCH_FETCH_ERROR)
      );
    }
  }
}
